import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConstants } from '../../commons/app-constants';

export interface DataAssistantCandidate {
  /** Exactly what gets resent as the next chat message when this option is picked — same text a
   *  user would have typed by hand, never an internal id. */
  value: string;
  name: string;
  contactPerson?: string | null;
  /** Added 2026-08-23 — what the user's own chat bubble should show after picking this option,
   *  when it needs to read differently from `value` (e.g. `value` is a raw backend intent code
   *  like "SHIPMENT_SUMMARY" for the "did you mean X or Y question type?" clarification — that
   *  code must still be what's resent, but must never be shown as-is). Null for every other
   *  candidate type (a real company name, or "Stock Unit"/"Consignment"/"Business Partner"), where
   *  `value`/`name` are already fine to show verbatim — fall back to `name` in that case. */
  displayText?: string | null;
}

export interface DataAssistantAskRequest {
  message: string;
  sessionId?: string | null;
}

export interface DataAssistantAskResponse {
  sessionId: string;
  answer: string;
  intent: string | null;
  resolved: boolean;
  data?: Record<string, unknown>;
  /** Real, existing screen this answer's "View Details" would open. Null when there's nothing to
   *  navigate to, or the turn is unresolved/a disambiguation question. */
  deepLink: string | null;
  attemptsUsed: number;
  /** Only present on a "which one did you mean?" / "did you mean X?" turn — reused as-is for the
   *  confidence-based "did you mean question type X or Y?" clarification added 2026-08-22, same
   *  shape, so no separate rendering path is needed for it. */
  candidates?: DataAssistantCandidate[];
  /** Added 2026-08-22 — the query log row id for this turn, needed to submit feedback on it. */
  queryLogId?: number | null;
  /** Added 2026-08-22 (Phase 2d) — the real credit cost of this turn. Null (not 0) when no real
   *  AI call happened this turn (a free disambiguation-resume answer, a greeting, or a block
   *  message) — genuinely free, not just unreported. */
  creditsUsed?: number | null;
  /** Added 2026-08-23 — "you might also ask" suggestions, only ever present on a genuine, fully
   *  resolved answer with no `candidates` pending. Same "tap sends this exact string as the next
   *  message" contract as a candidate's `value` — kept as a separate field so a UI never confuses
   *  these with the disambiguation chips. Empty (never absent-vs-empty meaningful) when nothing
   *  sensible to suggest. */
  relatedQuestions?: string[];
}

export interface DataAssistantSession {
  sessionId: string;
  title: string;
  lastActivity: string;
  turnCount: number;
}

export interface DataAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  deepLink: string | null;
  resolved: boolean;
}

export interface DataAssistantStatus {
  dataAssistantEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class AiDataAssistantService {
  private base = `${AppConstants.API_URL}api/v1/ai/data-assistant`;
  private appBase = `${AppConstants.API_URL}api/v1/app`;

  constructor(private http: HttpClient) {}

  /** Layer 1 of 2 (plan/tier gate) — layer 2 (RBAC) comes from the existing
   *  PermissionService.hasEffective('DATA_ASSISTANT_ACCESS'), already loaded app-wide. */
  checkTenantStatus(): Observable<DataAssistantStatus> {
    return this.http.get<DataAssistantStatus>(`${this.appBase}/data-assistant-status`);
  }

  ask(req: DataAssistantAskRequest): Observable<DataAssistantAskResponse> {
    return this.http.post<DataAssistantAskResponse>(`${this.base}/ask`, req);
  }

  getSessions(): Observable<DataAssistantSession[]> {
    return this.http.get<DataAssistantSession[]>(`${this.base}/sessions`);
  }

  getMessages(sessionId: string): Observable<DataAssistantMessage[]> {
    return this.http.get<DataAssistantMessage[]>(`${this.base}/sessions/${sessionId}/messages`);
  }

  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/sessions/${sessionId}`);
  }

  emailTranscript(sessionId: string, email?: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.base}/sessions/${sessionId}/email-transcript`,
      email ? { email } : {}
    );
  }

  /** Added 2026-08-22 — thumbs up/down on one answer. Same rating convention as the KB chat's
   *  own AiChatService.submitFeedback: 1 = up, -1 = down. */
  // creditsRewarded — added 2026-08-22. Always present, always 0 unless this was genuinely the
  // FIRST feedback ever given on this query and the reward is currently enabled — see the
  // backend's own note on why a rating change never grants it again.
  /** `reason` — added 2026-08-23, only meaningful alongside rating -1 (a thumbs-down "why" chip
   *  pick, see ai-chat-widget.component.ts selectDataFeedbackReason()) — omit entirely for the
   *  initial rating submit itself; the backend leaves any already-stored reason untouched when
   *  this key is absent, see AiDataAssistantController.submitFeedback's own note. */
  submitFeedback(queryLogId: number, rating: 1 | -1, reason?: string): Observable<{ message: string; creditsRewarded: number }> {
    return this.http.post<{ message: string; creditsRewarded: number }>(`${this.base}/queries/${queryLogId}/feedback`, { rating, reason });
  }
}
